import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AppRoute } from '@app/shared/utils/route-utils';
import { selectCurrentUserPrivileges } from '@core/auth2/store/auth2.reducer';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';

@Injectable({
  providedIn: 'root',
})
export class PrivilegeGuard  {
  private readonly store = inject(Store);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.routeHasRequiredPrivileges(route);
  }

  canLoad(route: Route): Observable<boolean> {
    return this.routeHasRequiredPrivileges(route);
  }

  protected routeHasRequiredPrivileges(route: AppRoute | ActivatedRouteSnapshot): Observable<boolean> {
    const requiredPrivileges: string[] | string = route.data.requiredPrivileges;

    return this.store.select(selectCurrentUserPrivileges).pipe(
      notNullish(), // Wait until the current user roles have been retrieved
      map((currentUserPrivileges) => {
        if (currentUserPrivileges == null)
          return false;

        return this.hasAllRequiredPrivileges(currentUserPrivileges, requiredPrivileges);
      }),
      take(1),
    );
  }

  private hasAllRequiredPrivileges(currentUserPrivileges: string[], requiredPrivileges: string[] | string) {
    if (typeof requiredPrivileges === 'string')
      requiredPrivileges = [ requiredPrivileges ];

    if (requiredPrivileges == null || requiredPrivileges.length === 0)
      return true;

    if (currentUserPrivileges == null || currentUserPrivileges.length === 0)
      return false;

    for (const requiredPrivilege of requiredPrivileges) {
      if (!currentUserPrivileges.includes(requiredPrivilege)) {
        console.warn(`Route activation failed.  User missing privilege: ${requiredPrivilege}`);
        return false;
      }
    }

    return true;
  }
}
